import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper";
import { Autoplay } from "swiper/modules";
import { useState, useRef, useEffect, useCallback } from "react";
import Hls from "hls.js";
import styles from "./Carousel.module.css";
import "swiper/css";

// A simple hook to detect mobile devices.
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const userAgent = typeof window.navigator === "undefined" ? "" : navigator.userAgent;
    const mobile = Boolean(userAgent.match(/Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i));
    setIsMobile(mobile);
  }, []);
  return isMobile;
};

// Hook to detect iOS devices
const useIsIOS = () => {
  const [isIOS, setIsIOS] = useState(false);
  useEffect(() => {
    const userAgent = typeof window.navigator === "undefined" ? "" : navigator.userAgent;
    const ios = Boolean(userAgent.match(/iPad|iPhone|iPod/));
    setIsIOS(ios);
  }, []);
  return isIOS;
};

export function Carousel({ carousels, itemClicked, children, hideVideos = false }) {
  const [activeIndex, setActiveIndex] = useState(0);
  // Use a single state object to reduce re-renders
  const [videoStates, setVideoStates] = useState({});
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [erroredVideos, setErroredVideos] = useState(new Set());
  const isMobile = useIsMobile();
  const isIOS = useIsIOS();
  const swiperRef = useRef(null);
  const videoRefs = useRef({});
  const hlsInstances = useRef({});

  // Check if there are multiple videos
  const videoCount = carousels ? carousels.filter(item => item.banner_type === 'video').length : 0;
  const hasMultipleVideos = videoCount > 1;

  // Helper to update video state for a specific index
  const setVideoState = useCallback((index, newState) => {
    setVideoStates(prev => ({
      ...prev,
      [index]: { ...(prev[index] || {}), ...newState }
    }));
  }, []);


  // Function to check if URL is HLS stream
  const isHLSUrl = (url) => {
    return url && (url.includes('.m3u8') || url.includes('application/vnd.apple.mpegurl'));
  };

  // Function to convert API URL to streaming URL for MP4 files
  const getStreamingUrl = (url) => {
    if (url && url.includes('/api/') && (url.includes('.mp4') || url.includes('.mov') || url.includes('.avi'))) {
      // Extract filename from URL and create streaming URL
      const filename = url.split('/').pop();
      return url.replace('/api/', '/api/v1/assets/stream/');
    }
    return url;
  };

  // Function to initialize HLS streaming
  const initializeHLS = (videoElement, url, index, item) => {
    if (Hls.isSupported() && isHLSUrl(url)) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 600
      });

      hls.loadSource(url);
      hls.attachMedia(videoElement);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        // HLS stream is ready
        console.log('HLS manifest parsed for video', index);
        // Trigger autoplay if this is the active slide
        if (index === activeIndex) {
          setTimeout(() => {
            try {
              const videoEl = videoRefs.current[index];
              if (videoEl && videoEl.paused) {
                videoEl.muted = isIOS ? true : false;
                videoEl.play().then(() => {
                  // Pause carousel autoplay when video starts playing
                  if (swiperRef.current?.swiper?.autoplay) {
                    swiperRef.current.swiper.autoplay.stop();
                  }
                }).catch(() => {
                  console.log('HLS autoplay failed');
                });
              }
            } catch (error) {
              console.error('Error during HLS video play:', error);
            }
          }, 100);
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS error:', data);
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              setErroredVideos(prev => new Set([...prev, item.ad_id]));
              break;
          }
        }
      });

      hlsInstances.current[index] = hls;
      return hls;
    }
    return null;
  };

  const carouselBlocks = !children && carousels && carousels.length ?
    // Sort carousels to show videos first, filter out errored videos and hide videos if hideVideos is true
    [...carousels].filter(item => !(item.banner_type === 'video' && (erroredVideos.has(item.ad_id) || hideVideos))).sort((a, b) => (a.banner_type === 'video' ? -1 : b.banner_type === 'video' ? 1 : 0)).map((item, index) => {
          if (item.banner_type === "video") {
            const state = videoStates[index] || {};
            const { isLoading, hasError, isPlaying, isMuted = false } = state;

            return (
              <SwiperSlide key={item.ad_id}>
                <div className={styles.justifyCenter} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => itemClicked(item)}>
                  <video
                    key={index}
                    ref={(el) => {
                      videoRefs.current[index] = el;
                      // Initialize HLS if needed
                      if (el && !hlsInstances.current[index] && isHLSUrl(item.ad_media)) {
                        initializeHLS(el, item.ad_media, index);
                      }
                    }}
                    src={isHLSUrl(item.ad_media) ? item.ad_media : (isIOS ? item.ad_media : getStreamingUrl(item.ad_media))}
                    width="100%"
                    height="200px"
                    controls={false}
                    disablePictureInPicture={true}
                    muted={true}
                    autoPlay={true}
                    loop={true}
                    playsInline={true}
                    preload="none"
                    className={styles.banner}
                    style={{
                      display: 'block',
                      minHeight: '200px',
                      objectFit: 'contain',
                      backgroundColor: '#000'
                    }}
                    onPlay={() => {
                      setVideoState(index, { isPlaying: true });
                    }}
                    onPause={() => setVideoState(index, { isPlaying: false })}
                    onVolumeChange={(e) => setVideoState(index, { isMuted: e.target.muted })}
                    onLoadStart={() => setVideoState(index, { isLoading: true, hasError: false })}
                    onLoadedData={() => {
                      setVideoState(index, { isLoading: false });
                      // Auto-play when data is loaded and it's the active slide
                      if (index === activeIndex) {
                        const video = videoRefs.current[index];
                        if (video && video.paused) {
                          video.muted = true; // Start muted, will unmute on play
                          video.play().then(() => {
                            // Pause carousel autoplay when video starts playing
                            if (swiperRef.current?.swiper?.autoplay) {
                              swiperRef.current.swiper.autoplay.stop();
                            }
                          }).catch(() => {
                            // Autoplay failed - user can manually start with controls
                          });
                        }
                      }
                    }}
                    onError={() => {
                      console.error('Video failed to load:', item.ad_media, 'at index', index);
                      setVideoState(index, { isLoading: false, hasError: true });
                      setErroredVideos(prev => new Set([...prev, item.ad_id]));
                    }}
                    poster="" // Prevent browser default poster
                  />
                  {isLoading && (
                    <div className={styles.videoOverlay}>Loading...</div>
                  )}
                  {hasError && (
                    <div className={styles.videoOverlay}>Could not load video.</div>
                  )}
                  {/* Custom play/pause and mute/unmute controls */}
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '10px',
                      right: '10px',
                      display: 'flex',
                      gap: '5px',
                      background: 'rgba(0,0,0,0.5)',
                      padding: '5px',
                      borderRadius: '8px',
                      zIndex: 10
                    }}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        try {
                          const video = videoRefs.current[index];
                          if (video) {
                            if (video.paused) {
                              video.muted = false; // Allow sound when manually playing
                              video.play().then(() => {
                                setHasUserInteracted(true); // Mark as interacted
                                // Pause carousel autoplay when video starts playing
                                if (swiperRef.current?.swiper?.autoplay) {
                                  swiperRef.current.swiper.autoplay.stop();
                                }
                              }).catch(() => {});
                            } else {
                              video.pause();
                            }
                          }
                        } catch (error) {
                          console.error('Error toggling video play:', error);
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        fontSize: '20px',
                        cursor: 'pointer'
                      }}
                    >
                      {isPlaying ? '⏸️' : '▶️'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        try {
                          const video = videoRefs.current[index];
                          if (video) {
                            video.muted = !video.muted;
                            setHasUserInteracted(true); // Mark as interacted
                          }
                        } catch (error) {
                          console.error('Error toggling video mute:', error);
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        fontSize: '20px',
                        cursor: 'pointer'
                      }}
                    >
                      {isMuted ? '🔇' : '🔊'}
                    </button>
                  </div>
                </div>
              </SwiperSlide>
            );
          }
          return (
            <SwiperSlide key={item.ad_id}>
              <a
                href={item.ad_target_url}
                target="_blank"
                rel="noreferrer"
                className={styles.justifyCenter}
                onClick={() => itemClicked(item)}
              >
                <img
                  src={item.ad_media}
                  alt="Offers"
                  className={styles.banner}
                  style={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover'
                  }}
                />
              </a>
            </SwiperSlide>
          );
        })
      : null; // prettier-ignore

  const handleTogglePlay = (index) => {
    const video = videoRefs.current[index];
    if (video) {
      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }
  };

  const handleToggleMute = (index) => {
    const video = videoRefs.current[index];
    if (video) {
      video.muted = !video.muted;
      // If unmuting, ensure autoplay is paused
      if (!video.muted && !video.paused) {
        if (swiperRef.current?.swiper?.autoplay) {
          swiperRef.current.swiper.autoplay.stop();
        }
      }
    }
  };

  // Function to handle video autoplay for active slide
  const handleVideoAutoplay = () => {
    if (carousels && carousels.length > 0) {
      // Get the sorted carousels to match the display order
      const sortedCarousels = [...carousels].sort((a, b) => (a.banner_type === 'video' ? -1 : b.banner_type === 'video' ? 1 : 0));
      const activeItem = sortedCarousels[activeIndex];

      // Pause all videos first
      Object.keys(videoRefs.current).forEach(index => {
        try {
          const video = videoRefs.current[index];
          if (video && !video.paused) {
            video.pause();
          }
        } catch (error) {
          console.error('Error pausing video:', error);
        }
      });

      // Autoplay the active video if it's a video
      if (activeItem?.banner_type === 'video') {
        try {
          const video = videoRefs.current[activeIndex];
          if (video && video.readyState >= 2) { // HAVE_CURRENT_DATA or higher
            // Start autoplay muted, will unmute on play event
            video.muted = true;

            video.play().then(() => {
              // Pause carousel autoplay when video starts playing
              if (swiperRef.current?.swiper?.autoplay) {
                swiperRef.current.swiper.autoplay.stop();
              }
            }).catch(() => {
              // Autoplay failed - user can manually start with controls
            });
          }
        } catch (error) {
          console.error('Error during video autoplay:', error);
        }
      } else {
        // Resume carousel autoplay when showing non-video content
        if (swiperRef.current?.swiper?.autoplay) {
          swiperRef.current.swiper.autoplay.start();
        }
      }
    }
  };

  useEffect(() => {
    // Handle video playback when active slide changes
    const swiperInstance = swiperRef.current?.swiper;
    if (swiperInstance) {
      // Use a small delay to ensure the slide transition is complete
      setTimeout(handleVideoAutoplay, 150);
    }
  }, [activeIndex, carousels]);

  useEffect(() => {
    // Handle initial video autoplay when component mounts and data is available
    if (carousels && carousels.length > 0) {
      // Small delay to ensure video elements are rendered
      const timer = setTimeout(() => {
        handleVideoAutoplay();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [carousels]);

  // Cleanup HLS instances on unmount
  useEffect(() => {
    return () => {
      Object.values(hlsInstances.current).forEach(hls => {
        if (hls) {
          hls.destroy();
        }
      });
    };
  }, []);


  // Dynamic configuration based on number of items
  const getSwiperConfig = () => {
    const itemCount = carousels?.length || 0;
    const baseConfig = {
      // Autoplay disabled to prevent automatic swiping
      autoplay: false,
      modules: [], // Remove Autoplay module since autoplay is disabled
    };

    if (itemCount === 1) {
      return {
        ...baseConfig,
        slidesPerView: 1.2,
        centeredSlides: true,
        loop: false, // Loop is problematic with a single item and can show blank space.
        allowTouchMove: true, // Allow swiping for better UX
      };
    } else if (itemCount === 2) {
      return {
        ...baseConfig,
        slidesPerView: 1.2,
        centeredSlides: true,
        loop: false, // Disable loop for 2 items to prevent back-to-back display
        allowTouchMove: true,
      };
    } else {
      return {
        ...baseConfig,
        slidesPerView: 1.5,
        centeredSlides: true,
        loop: true, // Loop is best with 3+ items
      };
    }
  };

  const swiperConfig = getSwiperConfig();

  return (
    <Swiper
      {...swiperConfig}
      spaceBetween={10}
      onSlideChange={(swiper) => {
        // Use realIndex for loop mode to get the correct index
        setActiveIndex(swiper.realIndex);
      }}
      ref={swiperRef}
    >
      {carouselBlocks}
      {children}
    </Swiper>
  );
}
